import React, { useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Carousel, { Modal, ModalGateway } from 'react-images';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import Button from '@mui/material/Button';
import BasementDehumidifierSetup from 'svg/img/gallery/mitigation/mitigation-basement-dehumidifier-setup.jpg';
import BasementDemoOverview from 'svg/img/gallery/mitigation/mitigation-basement-demo-overview.jpg';
import BasementEquipmentLineup from 'svg/img/gallery/mitigation/mitigation-basement-equipment-lineup.jpg';
import BasementWideDryingSetup from 'svg/img/gallery/mitigation/mitigation-basement-wide-drying-setup.jpg';
import BathroomDamageView1 from 'svg/img/gallery/mitigation/mitigation-bathroom-damage-view-1.jpg';
import BathroomDamageView2 from 'svg/img/gallery/mitigation/mitigation-bathroom-damage-view-2.jpg';
import DryingFanFloorView from 'svg/img/gallery/mitigation/mitigation-drying-fan-floor-view.jpg';
import ExposedFramingDrying1 from 'svg/img/gallery/mitigation/mitigation-exposed-framing-drying-1.jpg';
import ExposedFramingDrying2 from 'svg/img/gallery/mitigation/mitigation-exposed-framing-drying-2.jpg';
import ExposedFramingDrying3 from 'svg/img/gallery/mitigation/mitigation-exposed-framing-drying-3.jpg';
import ExteriorExcavationWork from 'svg/img/gallery/mitigation/mitigation-exterior-excavation-work.jpg';
import FinishedCeilingRemoval from 'svg/img/gallery/mitigation/mitigation-finished-ceiling-removal.jpg';
import FramingAndDryingLayout from 'svg/img/gallery/mitigation/mitigation-framing-and-drying-layout.jpg';
import LowerLevelDryingFan from 'svg/img/gallery/mitigation/mitigation-lower-level-drying-fan.jpg';
import MainLevelAirMover from 'svg/img/gallery/mitigation/mitigation-main-level-air-mover.jpg';
import MechanicalRoomDrying from 'svg/img/gallery/mitigation/mitigation-mechanical-room-drying.jpg';
import MoistureMeterCheck from 'svg/img/gallery/mitigation/mitigation-moisture-meter-check.jpg';
import OpenSpaceCleanout from 'svg/img/gallery/mitigation/mitigation-open-space-cleanout.jpg';
import OpenWallWorkArea from 'svg/img/gallery/mitigation/mitigation-open-wall-work-area.jpg';
import StairFramingOverview from 'svg/img/gallery/mitigation/mitigation-stair-framing-overview.jpg';
import WaterDamageHallway from 'svg/img/gallery/mitigation/mitigation-water-damage-hallway.jpg';
import WaterDamagePartitionWall from 'svg/img/gallery/mitigation/mitigation-water-damage-partition-wall.jpg';
import WaterDamageStairwell from 'svg/img/gallery/mitigation/mitigation-water-damage-stairwell.jpg';
import WaterDamageWindowWall from 'svg/img/gallery/mitigation/mitigation-water-damage-window-wall.jpg';

const Gallery = () => {
  const theme = useTheme();
  const [currentImage, setCurrentImage] = useState(0);
  const [viewerIsOpen, setViewerIsOpen] = useState(false);

  const openLightbox = (index) => {
    setCurrentImage(index);
    setViewerIsOpen(true);
  };

  const closeLightbox = () => {
    setCurrentImage(0);
    setViewerIsOpen(false);
  };

  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  const photos = [
    {
      src: BasementDemoOverview,
      alt: 'Mitigation project basement overview with open framing and drying equipment in place.',
      rows: 2,
      cols: 1,
    },
    {
      src: DryingFanFloorView,
      alt: 'Concrete basement floor after mitigation setup with an air mover near the open wall framing.',
      rows: 1,
      cols: 2,
    },
    {
      src: OpenWallWorkArea,
      alt: 'Mitigation work area with open walls, exposed framing, and a drying fan on the floor.',
      rows: 1,
      cols: 1,
    },
    {
      src: BasementWideDryingSetup,
      alt: 'Wide basement mitigation view showing multiple air movers and an opened lower wall section.',
      rows: 1,
      cols: 1,
    },
    {
      src: ExposedFramingDrying2,
      alt: 'Exposed wall framing during mitigation with drying equipment set across the room.',
      rows: 1,
      cols: 2,
    },
    {
      src: OpenSpaceCleanout,
      alt: 'Cleared mitigation space with exposed structure and a drying fan positioned in the room.',
    },
    {
      src: StairFramingOverview,
      alt: 'Basement stair area after mitigation demolition with framing and subfloor visible.',
    },
    {
      src: BasementDehumidifierSetup,
      alt: 'Mitigation dehumidifier operating in an open basement work area.',
    },
    {
      src: FramingAndDryingLayout,
      alt: 'Open basement framing with drying equipment arranged to circulate air through the space.',
    },
    {
      src: MechanicalRoomDrying,
      alt: 'Mechanical room area during mitigation with exposed framing and equipment drying the space.',
    },
    {
      src: FinishedCeilingRemoval,
      alt: 'Mitigation cleanup after ceiling removal with open framing visible along the stair landing.',
    },
    {
      src: MainLevelAirMover,
      alt: 'Main level mitigation setup with an air mover drying the floor near a staircase.',
    },
    {
      src: LowerLevelDryingFan,
      alt: 'Lower level mitigation scene with a floor fan drying an open living area.',
    },
    {
      src: MoistureMeterCheck,
      alt: 'Moisture meter reading being taken during mitigation to verify drying progress.',
    },
    {
      src: ExteriorExcavationWork,
      alt: 'Exterior mitigation work with excavation equipment beside the home.',
    },
    {
      src: WaterDamageStairwell,
      alt: 'Severe water damage visible around a stairwell wall before mitigation cleanup.',
    },
    {
      src: WaterDamageWindowWall,
      alt: 'Water-damaged wall surface near a basement window before mitigation demolition.',
    },
    {
      src: WaterDamagePartitionWall,
      alt: 'Water damage across a partition wall before the mitigation work begins.',
    },
    {
      src: WaterDamageHallway,
      alt: 'Flood-damaged hallway area with standing water and reflective wet wall surfaces.',
    },
    {
      src: BathroomDamageView1,
      alt: 'Bathroom wall damage documented before mitigation cleanup and drying.',
    },
    {
      src: BathroomDamageView2,
      alt: 'Second bathroom damage view showing the affected wall and fixtures before cleanup.',
    },
    {
      src: BasementEquipmentLineup,
      alt: 'Mitigation equipment lined up beside a damaged basement wall during drying.',
    },
    {
      src: ExposedFramingDrying1,
      alt: 'Open framing and mitigation equipment drying a stripped wall cavity.',
    },
    {
      src: ExposedFramingDrying3,
      alt: 'Additional exposed framing view with drying fans positioned around the room.',
    },
  ];

  const photosToShow = isMd ? photos.slice(0, 5) : photos.slice(0, 4);

  return (
    <Box>
      <Box display={'flex'} justifyContent={'flex-end'} marginBottom={2}>
        <Button
          color="primary"
          size="large"
          endIcon={
            <svg
              width={16}
              height={16}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          }
          onClick={() => openLightbox(0)}
        >
          View mitigation gallery
        </Button>
      </Box>
      <Box>
        <ImageList
          variant="quilted"
          cols={4}
          rowHeight={isMd ? 300 : 220}
          gap={isMd ? 16 : 8}
          sx={{ overflowY: 'visible' }}
        >
          {photosToShow.map((item, i) => (
            <ImageListItem
              key={i}
              cols={isMd ? item.cols || 1 : 2}
              rows={isMd ? item.rows || 1 : 1}
            >
              <LazyLoadImage
                height={'100%'}
                width={'100%'}
                src={item.src}
                alt={item.alt}
                title={item.alt}
                effect="blur"
                onClick={() => openLightbox(i)}
                style={{
                  objectFit: 'cover',
                  cursor: 'pointer',
                  borderRadius: 4,
                }}
              />
            </ImageListItem>
          ))}
        </ImageList>
      </Box>
      <ModalGateway>
        {viewerIsOpen ? (
          <Modal onClose={closeLightbox}>
            <Carousel
              currentIndex={currentImage}
              views={photos.map((x) => ({
                ...x,
                caption: x.alt,
              }))}
            />
          </Modal>
        ) : null}
      </ModalGateway>
    </Box>
  );
};

export default Gallery;
